<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <ModuleShell :title="$t('views.selectedGoat.temperature.listTitle')"
               :icon="List"
               :hide-action="true">
    <ValueListTable :data="temperatures"
                    :columns="columns"
                    :loading="false"
                    @refresh="refreshTemperatures">
      
      <template #cell-value="{ item }">
        <span class="font-bold">
          {{ (item as TemperatureEntry).value }} °C
        </span>
      </template>

      <template #cell-actions="{ item }">
        <div class="flex items-center justify-end gap-2">
          
          <button 
            @click.stop="openEditDialog(item as TemperatureEntry)"
            class="h-8 w-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer bg-[var(--color-primary-200)] text-[var(--color-primary-700)] hover:bg-[var(--color-primary-300)]"
            :title="$t('common.buttons.edit')"
          >
            <Pencil class="w-4 h-4" />
          </button>

          <button 
            @click.stop="openDeleteDialog(item as TemperatureEntry)"
            class="h-8 w-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer bg-[var(--color-primary-200)] text-[var(--color-error)] hover:bg-[var(--color-error)] hover:text-white"
            :title="$t('common.buttons.delete')"
          >
            <Trash2 class="w-4 h-4" />
          </button>

        </div>
      </template>

      <template #toolbar-actions>
        <button @click="openAddDialog"
                class="btn h-[42px] px-4 rounded-lg flex items-center gap-2 font-medium text-white shadow-sm transition-transform active:scale-95-white"
                :style="{
                  backgroundColor: 'var(--color-success)',
                  border: '1px solid var(--color-success)'
                }">
          <Plus class="w-5 h-5" />
          <span>{{ $t('common.buttons.add') }}</span>
        </button>
      </template>
    </ValueListTable>

    <Teleport to="body">
      <ConfirmActionModule v-if="showConfirm && selectedTemperature"
                           :message="$t(
                            'views.selectedGoat.temperature.deleteAskMessage',
                            { 
                              date: formatDate(selectedTemperature.measuredAt),
                              temp: selectedTemperature.value,
                             }
                          )"
                           :confirmMessage="$t('common.buttons.delete')"
                           variant="danger"
                           :confirmLabel="$t('common.buttons.delete')"
                           @cancel="closeConfirm"
                           @confirm="deleteConfirmed" />
    </Teleport>

  </ModuleShell>

  <ValueInput :show="showDialog"
              :title="isEditing ? $t('common.buttons.edit') : (goat?.eartag || '')"
              :subtitle="goat?.name || ''"
              :inputLabel="$t('views.session.temperature.label')"
              :placeholder="$t('views.session.temperature.placeholder')"
              :inputMode="'decimal'"
              :goatId="goatId"
              :validation="{ type: 'range', min: 35, max: 45, unit: '°C' }"
              :showDateInput="true"
              :initialValue="isEditing ? selectedTemperature?.value : null"
              @save="handleTemperatureSaved"
              @cancel="showDialog = false" />
</template>

<script setup
        lang="ts">
        import { ref, computed } from 'vue'
        import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
        import ValueInput from '@/components/ui/inputs/ValueInput.vue'
        import { List, Plus, Pencil, Trash2 } from 'lucide-vue-next'
        import { useTemperatureStore } from '@/stores/temperature.store.js'
        import ValueListTable from '@/components/ui/tables/ValueListTable.vue'
        import { useI18n } from 'vue-i18n'
        import { useGoatStore } from '@/stores/goat.store.js'
        import ConfirmActionModule from '@/components/actions/ConfirmAction.vue'
        import { formatDate } from '@/utils/format/dateformatting'

        const { t } = useI18n()

        interface TemperatureEntry {
          id: number
          value: number
          measuredAt: string
          goatId: number
        }

        const columns = [
          { key: 'measuredAt', label: t('common.valueTableList.temperatureDate'), type: 'date' },
          { key: 'value', label: t('common.valueTableList.temperatureValue'), center: true, type: 'number' },
          { key: 'actions', label: '', right: true }
        ]

        const props = defineProps<{
          temperatures: TemperatureEntry[]
          goatId: number
          refreshTemperatures: () => Promise<void> | void
        }>()


        const temperatureStore = useTemperatureStore()
        const goatStore = useGoatStore()
        
        const showDialog = ref(false)
        const showConfirm = ref(false)
        const isEditing = ref(false)
        const selectedTemperature = ref<TemperatureEntry | null>(null)

        const goat = computed(() => {
          return goatStore.goats.find(g => g.id === props.goatId)
        })

        function openAddDialog() {
          selectedTemperature.value = null
          isEditing.value = false
          showDialog.value = true
        }

        function openEditDialog(item: TemperatureEntry) {
          selectedTemperature.value = item
          isEditing.value = true
          showDialog.value = true
        }

        function openDeleteDialog(item: TemperatureEntry) {
          selectedTemperature.value = item
          showConfirm.value = true
        }

        function closeConfirm() {
          showConfirm.value = false
          selectedTemperature.value = null
        }

        async function handleTemperatureSaved({ processedValue, date }: { processedValue: number, date: string }) {
          const measuredAt = date
            ? new Date(date + 'T' + new Date().toTimeString().split(' ')[0]).toISOString()
            : new Date().toISOString()

          try {
            if (isEditing.value && selectedTemperature.value) {
              // UPDATE
              await temperatureStore.updateTemperature(selectedTemperature.value.id, props.goatId, processedValue, measuredAt)
            } else {
              // CREATE
              await temperatureStore.addTemperature(props.goatId, processedValue, measuredAt)
            }
            showDialog.value = false
            await props.refreshTemperatures()
          } catch (error) {
            console.error('Failed to save temperature:', error)
          }
        }

        async function deleteConfirmed() {
          if (!selectedTemperature.value) return

          try {
            await temperatureStore.deleteTemperature(
              selectedTemperature.value.id,
              props.goatId
            )
            await props.refreshTemperatures()
            closeConfirm()
          } catch (e) {
            console.error('Failed to delete temperature:', e)
          }
        }

</script>

<style scoped></style>